import Title from "common/Typography/Title";
import Text from "common/Typography/Text";
import React, { CSSProperties, ReactNode, useContext } from "react";
import styled, { useTheme } from "styled-components";
import Button from "common/Buttons/Button";
import { useSelector } from "react-redux";
import { FiltersSlice, LanguageNameSpaces } from "@shared/types";
import { PageWrapperContext } from "common/Home/PageWrapper/PageWrapper.context.tsx";
import useMedia from "utils/hooks/useMedia";
import { MOBILE_FILTERS } from "common/constants";
import Link from "common/Typography/Link";
import { EmptyStatesType } from "common/Icons/types.ts";
import { useEmptyState } from "common/Icons/useEmptyState.tsx";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";
import { getNewLookTokens, newLookType } from "common/NewLook/tokens.ts";

interface EmptyViewProps {
  iconKey?: EmptyStatesType;
  icon?: React.JSX.Element;
  iconStyle?: CSSProperties;
  title?: string;
  description?: string;
  buttonText?: string;
  buttonIcon?: React.JSX.Element;
  buttonOnClick?: () => void;
  buttonLoading?: boolean;
  isLinkButton?: boolean;
  iconWidth?: number;
  objectName?: string;
  hasData?: boolean;
  isSearch?: boolean;
  filtersSlice?: FiltersSlice;
  openFilters?: () => void;
  filtersChipsOpen?: string;
  style?: CSSProperties;
  titleStyle?: CSSProperties;
  descriptionStyle?: CSSProperties;
  buttonStyle?: CSSProperties;
}

const EmptyView = ({
  iconKey,
  icon: propsIcon,
  iconStyle,
  title,
  description,
  buttonText,
  buttonIcon,
  buttonOnClick,
  buttonLoading,
  isLinkButton,
  iconWidth,
  objectName,
  hasData,
  isSearch,
  filtersSlice,
  openFilters,
  filtersChipsOpen,
  style,
  titleStyle,
  descriptionStyle,
  buttonStyle,
}: EmptyViewProps) => {
  const { t } = useTypedTranslation(LanguageNameSpaces.COMMON);
  const { isNewLook } = useTheme();
  const isFiltersChipsOpen = useMedia(
    [`(max-width: ${filtersChipsOpen})`],
    [true],
    false
  );
  const isFilterColumnOpen = useMedia(
    [`(min-width: ${MOBILE_FILTERS})`],
    [true],
    false
  );

  const numActiveFilters = useSelector(
    filtersSlice?.selectors.selectNumActiveFilters || (() => undefined)
  );
  const { setMobileFiltersOpen } = useContext(PageWrapperContext);
  const EmptyStates = useEmptyState();

  let icon: ReactNode | undefined;
  if (hasData || hasData === undefined) {
    if (isSearch) {
      icon = <EmptyStates.FileSearch style={{ maxWidth: 140 }} />;
      title = t("empty.no_results_matching_search", {
        objectName: objectName || t("empty.results"),
      });
      description = undefined;
      buttonText = undefined;
    } else if (numActiveFilters) {
      icon = <EmptyStates.Filters style={{ maxWidth: 160 }} />;
      title = t("empty.no_results_matching_filters", {
        objectName: objectName || t("empty.results"),
      });
      description = undefined;
      buttonText =
        isFiltersChipsOpen || (!openFilters && isFilterColumnOpen)
          ? undefined
          : t("empty.open_filters");
      if (openFilters) buttonOnClick = openFilters;
      else {
        buttonOnClick = () => setMobileFiltersOpen(true);
      }
    }
  }

  const button = isLinkButton ? (
    <Link style={{ marginTop: 4 }} onClick={buttonOnClick}>
      {buttonText}
    </Link>
  ) : (
    <StyledButton
      type="primary"
      onClick={buttonOnClick}
      loading={buttonLoading}
      style={buttonStyle}
    >
      {buttonIcon}
      {buttonText}
    </StyledButton>
  );

  const EmptyIcon = iconKey ? EmptyStates[iconKey] : undefined;
  const emptyIcon = EmptyIcon ? <EmptyIcon /> : undefined;
  const iconNode = icon || propsIcon || emptyIcon;

  // New look: a quiet mark, the title in the panel-title role, one meta line
  // and at most one normal-size action.
  if (isNewLook) {
    return (
      <NewLookWrapper style={{ ...style }}>
        {iconNode && (
          <Icon $width={iconWidth ?? NEW_LOOK_ICON_WIDTH} style={iconStyle}>
            {iconNode}
          </Icon>
        )}
        {title && <NewLookTitle style={titleStyle}>{title}</NewLookTitle>}
        {description && (
          <NewLookDescription style={descriptionStyle}>
            {description}
          </NewLookDescription>
        )}
        {buttonText &&
          (isLinkButton ? (
            <Link style={{ marginTop: 8 }} onClick={buttonOnClick}>
              {buttonText}
            </Link>
          ) : (
            <NewLookButton
              type="primary"
              onClick={buttonOnClick}
              loading={buttonLoading}
              style={buttonStyle}
            >
              {buttonIcon}
              {buttonText}
            </NewLookButton>
          ))}
      </NewLookWrapper>
    );
  }

  return (
    <EmptyWrapper style={{ ...style }}>
      <Icon $width={iconWidth} style={iconStyle}>
        {iconNode}
      </Icon>
      {title && (
        <Title level={3} style={{ marginTop: 8, ...titleStyle }}>
          {title}
        </Title>
      )}
      <Text type="secondary" style={descriptionStyle}>
        {description}
      </Text>
      {buttonText && button}
    </EmptyWrapper>
  );
};

export default EmptyView;

const EmptyWrapper = styled.div`
  padding: 48px 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  justify-content: center;
  height: 100%;
`;
const Icon = styled.div<{ $width?: number }>`
  ${(p) =>
    p.theme.isNewLook
      ? `
    > svg {
      width: ${p.$width ?? 136}px;
      height: auto;
    }
  `
      : `
    svg {
      width: ${p.$width ?? 160}px;
      height: auto;
    }
  `}
`;
const StyledButton = styled(Button)`
  padding: 4px 64px;
  margin-top: 20px;
`;
const NEW_LOOK_ICON_WIDTH = 96;
const NewLookWrapper = styled(EmptyWrapper)`
  padding: 40px 20px;
`;
const NewLookTitle = styled.div`
  margin-top: 12px;
  ${newLookType.panelTitle};
  color: ${(p) => getNewLookTokens(p.theme).ink};
`;
const NewLookDescription = styled.div`
  max-width: 400px;
  margin-top: 4px;
  ${newLookType.meta};
  color: ${(p) => getNewLookTokens(p.theme).muted};
`;
const NewLookButton = styled(Button)`
  margin-top: 16px;
  min-width: 140px;
`;
